<script setup lang="ts">
import { useWadObserver } from '~/composables/useWadObserver'
import { useTracking } from '~/composables/useTracking'
import { useImageCdn } from '~/composables/useImageCdn'

interface Ad {
  type?: string
  entity_id: string
  entity_slug?: string
  entity_url?: string
  entity_logo_url: string
  entity_text: string
  entity_tracking_click: string
  entity_tracking_view: string
  company_name: string
}

const props = defineProps<{
  ad: Ad
  baseUrl: string
}>()

const { trackEvent } = useTracking()
const { fixedWidthImageParams } = useImageCdn()

// Setup observer for view tracking
const elRef = useTemplateRef('elRef')

// Helpers
const getUrl = (): string => {
  if (props.ad.type === 'company') {
    return `${props.baseUrl}/en/companies/${props.ad.entity_id}/${props.ad.entity_slug ?? ''}`
  }
  return props.ad.entity_url || ''
}

const trackClick = (): void => {
  trackEvent(props.ad.entity_tracking_click, { entity_id: Number(props.ad.entity_id) })
}

useWadObserver(elRef, () => {
  trackEvent(props.ad.entity_tracking_view, { entity_id: Number(props.ad.entity_id) })
})
</script>

<template>
  <a class="wad4-ads__link wad4-ads__wrapper" :href="getUrl()" target="_blank" @click.stop="trackClick" ref="elRef">
    <img class="wad4-ads__image" :class="{'wad4-ads__image--round': props.ad.type === 'company'}" :alt="props.ad.company_name" loading="lazy" v-bind="fixedWidthImageParams(props.ad.entity_logo_url, 100, 1)"/>
    <div class="wad4-ads__content">
      <div class="wad4-ads__text">
        {{ props.ad.entity_text }}
      </div>
      <div class="wad4-ads__cta-text">
        {{ props.ad.entity_cta }}
      </div>
    </div>
  </a>
</template>

<style lang="scss">
.wad4-ads {
  &__link {
    width: $wad4-col-6;
    text-decoration: none;
    @include mqBreakPoint($wad4-breakpoint-mobile-max) {
      width: $wad4-col-4;
    }
  }

  &__wrapper {
    display: flex;
    padding: 12px;
    align-items: center;
    gap: 24px;
    border-radius: 4px;
    background: $wad4-c-background-bg-primary;
    border: 1px solid  $wad4-c-border-border-subtle;
    text-decoration: none;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06);
    transition: border-color 0.2s ease;
    width: $wad4-col-3;
    @include mqBreakPoint(0, $wad4-breakpoint-desktop-max) {
      width: $wad4-col-6;
    }
    @include mqBreakPoint(0, $wad4-breakpoint-tablet-max) {
      width: $wad4-col-4;
    }

    &:hover {
      background-color: $wad4-c-background-bg-secondary;
      border: 1px solid $wad4-c-border-border-brand-solid;

      .wad4-ads__text  {
        color: $wad4-c-text-text-brand-tertiery;
      }

    }
  }

  &__image {
    flex: none;

    &--round {
      border-radius: 50%;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: $wad4-spacing-sm;
    flex: 1;
    overflow: hidden;
    overflow-wrap: break-word;
  }

  &__text,
  &__cta-text {
    @include text-size-mixin('md');
  }

  &__text {
    color: $wad4-c-text-text-primary;
    font-weight: $wad4-font-weight-semi-bold;
  }

  &__cta-text {
    color: $wad4-c-text-text-secondary;
    text-decoration-line: underline;
  }
}

</style>
